import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send, Loader2, ImagePlus } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useLocale } from "@/context/LocaleContext";

export default function StylistPanel() {
  const { formatMoney } = useLocale();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi, I'm Shoptosa — your personal shopper. Tell me what you need, or upload a photo of something you like and I'll pull matching items from across the marketplace." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [attachPath, setAttachPath] = useState(null);
  const [attachPreview, setAttachPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachPreview(URL.createObjectURL(file));
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", "stylist");
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachPath(data.path);
    } catch (err) {
      toast.error(apiError(err));
      setAttachPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const q = input.trim();
    if ((!q && !attachPath) || loading) return;
    const userMsg = { role: "user", text: q || "Find me looks like this", image: attachPreview };
    setMessages((m) => [...m, userMsg]);
    const imagePath = attachPath;
    setInput("");
    setAttachPath(null);
    setAttachPreview(null);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/stylist", { query: q || "Find items matching this outfit photo", image_path: imagePath });
      setMessages((m) => [...m, { role: "assistant", text: data.message, products: data.products }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I had trouble styling that. Try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="stylist-trigger"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 pl-4 pr-5 py-3 rounded-full btn-accent shadow-[0_8px_30px_rgb(123,30,56,0.35)]"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-medium">Ask Shoptosa</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="stylist-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-md h-full glass border-l border-white/40 flex flex-col fade-up">
            <div className="flex items-center justify-between px-6 py-5 border-b border-aura-border">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-aura-accent" />
                <span className="font-serif text-xl">Shoptosa Stylist</span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="stylist-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "text-right" : ""}>
                  {m.image && (
                    <img src={m.image} alt="attached" className="inline-block w-24 h-32 object-cover rounded-2xl mb-2 border border-aura-border" />
                  )}
                  <div
                    className={`inline-block max-w-[85%] text-sm leading-relaxed px-4 py-3 ${
                      m.role === "user"
                        ? "bg-aura-accent text-white rounded-2xl rounded-br-sm"
                        : "bg-white/5 rounded-2xl rounded-bl-sm border border-aura-border"
                    }`}
                  >
                    {m.text}
                  </div>
                  {m.products?.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      {m.products.map((p) => (
                        <Link
                          key={p.id}
                          to={`/product/${p.id}`}
                          onClick={() => setOpen(false)}
                          className="group text-left"
                          data-testid={`stylist-match-${p.id}`}
                        >
                          <div className="aspect-[3/4] overflow-hidden bg-aura-sand mb-1.5">
                            <img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover img-zoom" />
                          </div>
                          <div className="text-xs font-serif leading-tight">{p.title}</div>
                          {p.external && <div className="text-[10px] uppercase tracking-luxe text-aura-muted">Partner</div>}
                          <div className="text-[11px] font-mono text-aura-muted">{formatMoney(p.price, p.currency || "USD")}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-sm text-aura-muted">
                  <Loader2 className="w-4 h-4 animate-spin" /> Styling your look…
                </div>
              )}
            </div>

            <div className="p-4 border-t border-aura-border">
              {attachPreview && (
                <div className="mb-3 flex items-center gap-2">
                  <div className="relative">
                    <img src={attachPreview} alt="attachment" className="w-12 h-16 object-cover rounded border border-aura-border" />
                    {uploading && <div className="absolute inset-0 bg-white/5 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>}
                  </div>
                  <button onClick={() => { setAttachPath(null); setAttachPreview(null); }} className="text-xs text-aura-muted link-underline">Remove</button>
                </div>
              )}
              <div className="flex items-center gap-2 bg-aura-surface rounded-full border border-aura-border pl-2 pr-2 py-1.5">
                <button onClick={() => fileRef.current?.click()} data-testid="stylist-attach" className="w-9 h-9 rounded-full hover:bg-aura-sand flex items-center justify-center shrink-0">
                  <ImagePlus className="w-4 h-4 text-aura-muted" />
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="stylist-file-input" />
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="e.g. summer wedding guest under $300"
                  className="flex-1 bg-transparent outline-none text-sm"
                  data-testid="stylist-input"
                />
                <button
                  onClick={send}
                  disabled={loading}
                  data-testid="stylist-send"
                  className="w-9 h-9 rounded-full btn-accent flex items-center justify-center disabled:opacity-50 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
